import { useEffect, useMemo } from "react";
import seo from "@/data/seo.json";
import states from "@/data/states.json";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const SITE_NAME = "SpaceX Child Shares News";
export const AD_EMAIL = "spacexchildshares@outlook.com";
export const X_SEARCH = (q) => `https://x.com/search?q=${encodeURIComponent(q)}&f=live`;

const fill = (t, s) => t.replace(/\{state\}/g, s.name).replace(/\{utma\}/g, s.utma).replace(/\{majority\}/g, s.majority);

export const stateSeo = (s) => ({
  path: `/custodial-accounts/${s.slug}`, name: `${s.name} Custodial Accounts`,
  title: fill(seo.stateTemplate.title, s), description: fill(seo.stateTemplate.description, s), h1: fill(seo.stateTemplate.h1, s),
});

export const pageSeo = (path) => {
  const m = path.match(/^\/custodial-accounts\/([a-z-]+)$/);
  const st = m && states.find((s) => s.slug === m[1]);
  if (st) return stateSeo(st);
  if (path.startsWith("/news/")) return { ...seo.pages[0], path, name: "News story" };
  const known = seo.pages.find((p) => p.path === path);
  if (!known && path !== "/") return { path, name: "Page not found", title: `Page not found | ${SITE_NAME}`, description: seo.pages[0].description, missing: true };
  return seo.pages.find((p) => p.path === path) || seo.pages[0];
};

function setTag(selector, attr, key, value) {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement(selector.startsWith("link") ? "link" : "meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute(selector.startsWith("link") ? "href" : "content", value);
}

export function applySeo(page) {
  const url = window.location.origin + (page.path === "/" ? "/" : page.path);
  const image = page.image || window.location.origin + seo.defaultImage;
  document.title = page.title;
  setTag('meta[name="description"]', "name", "description", page.description);
  setTag('link[rel="canonical"]', "rel", "canonical", url);
  setTag('meta[property="og:title"]', "property", "og:title", page.title);
  setTag('meta[property="og:description"]', "property", "og:description", page.description);
  setTag('meta[property="og:url"]', "property", "og:url", url);
  setTag('meta[property="og:image"]', "property", "og:image", image);
  setTag('meta[name="twitter:title"]', "name", "twitter:title", page.title);
  setTag('meta[name="twitter:description"]', "name", "twitter:description", page.description);
  setTag('meta[name="twitter:image"]', "name", "twitter:image", image);
  setTag('meta[property="og:type"]', "property", "og:type", page.published ? "article" : "website");
  document.head.querySelectorAll('meta[property^="article:"]').forEach((m) => m.remove());
  if (page.published) setTag('meta[property="article:published_time"]', "property", "article:published_time", page.published);
}

export function useRouteSeo(path) {
  const page = useMemo(() => pageSeo(path), [path]);
  useEffect(() => {
    applySeo(page);
    window.scrollTo(0, 0);
  }, [page]);
  const crumbs = useMemo(() => { const c = crumbsFor(page.path); return c && breadcrumbLd(c); }, [page]);
  useJsonLd("ld-breadcrumbs", crumbs);
}

// Breadcrumb trail for a route; article pages build their own (they need the story title).
export function crumbsFor(path) {
  if (path === "/" || path.startsWith("/news/")) return null;
  const page = pageSeo(path);
  if (page.path === "/" || page.missing) return null;
  const list = [{ name: "Home", path: "/" }];
  if (path.startsWith("/custodial-accounts/")) list.push({ name: "State Guides", path: "/custodial-accounts" });
  if (path.startsWith("/topics/")) list.push({ name: "Latest News", path: "/latest-news" });
  if (path === "/trump-accounts-calculator") list.push({ name: "Trump Accounts Guide", path: "/trump-accounts-guide" });
  list.push({ name: page.name, path: page.path });
  return list;
}

export const breadcrumbLd = (list) => ({
  "@context": "https://schema.org", "@type": "BreadcrumbList",
  itemListElement: list.map((c, i) => ({ "@type": "ListItem", position: i + 1, name: c.name, item: window.location.origin + c.path })),
});

export function useJsonLd(id, data) {
  useEffect(() => {
    if (!data) return;
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(data);
    return () => el.remove();
  }, [id, data]);
}

export function timeAgo(iso) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 3600) return `${Math.max(1, Math.round(diff / 60))}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}
